import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { snakecaseKeys } from '@src/util/functions'
import SessionManager from '@src/lib/session_manager'
import { TrainingFrequencyResponse } from '@src/routes/members/components/status/training-frequency'
import Member, { RecentActivity, TrainingPlanProgress } from '@src/data/member'
import camelcaseKeys from 'camelcase-keys'
import MemberMeta from '@src/data/member-meta'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'

type MemberResponse = { member: Member }
export type MembersResponse = { members: Member[], meta: MemberMeta }
type MemberDetailsResponse = { members: MemberDetail[] }
type MemberDetail = {
  id: number,
  recent_activity: RecentActivity,
  training_plan_progress: TrainingPlanProgress
}
const REQUEST_LIMIT = 300

export class MemberService {

  apiClient: ApiClient = new ApiClient()

  getMembers (params = ''): Promise<(MembersResponse)> {
    const url = params ? API.TRAINERS_API.MEMBERS + '?' + params : API.TRAINERS_API.MEMBERS
    return this.apiClient.get<MembersResponse>(url)
      .then((response) => {
        const members = response.members.map((attrs) => new Member(camelcaseKeys(attrs)))
        if (response.meta) {
          const meta = new MemberMeta({
            ...camelcaseKeys(response.meta),
            counters: camelcaseKeys(response.meta.counters)
          })
          return { members, meta }
        }
        return { members, meta: { totalPages: 0, currentPage: 0, counters: null } }
      })
      .catch((error) => {
        console.error('failed to retrieve members', error)
        return Promise.reject(error)
      })
  }

  async getMembersDetail (ids: number[]): Promise<(any)> {
    let currentIndex = 0
    const memberDetailsList: any[] = []
    while (currentIndex < ids.length) {
      await this.apiClient.get<MemberDetailsResponse>(API.TRAINERS_API.MEMBERS_LATEST_ACTIVITY_DETAILS, { member_ids: ids.slice(currentIndex, currentIndex + REQUEST_LIMIT) })
        .then((memberDetails) => {
          memberDetailsList.push.apply(memberDetailsList, memberDetails.members.map(mDetail => camelcaseKeys(mDetail, { deep: true })))
          currentIndex += REQUEST_LIMIT
        })
        .catch(error => {
          console.log('failed to retrieve members details', error)
          return Promise.reject(error)
        })
    }
    return memberDetailsList
  }

  updatePersonalInformation (member, memberId ?) {
    if (SessionManager.Instance.isManager()) {
      return this.updateMember(member, memberId)
    } else {
      return this.updateProfile(member)
    }
  }

  updateEmail (email) {
    return this.apiClient.put(SessionManager.Instance.isManager() ? `${API.TRAINERS_API.EMAIL}` : API.MEMBERS_API.EMAIL, { email: email })
      .then(() => Promise.resolve())
      .catch(error => {
        console.log('error saving email updates', error)
        return Promise.reject(error)
      })
  }

  updateSettings (memberId, settings) {
    if (SessionManager.Instance.isManager()) {
      return this.updateMember(settings, memberId)
    } else {
      return this.updateProfile(settings)
    }
  }

  getTrainingFrequency (memberId, timeFrame, periods) {
    let path = (SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_FREQUENCY_YEARLY : API.MEMBERS_API.TRAINING_FREQUENCY_YEARLY).replace('{memberId}', `${memberId}`)
    if (timeFrame === DateFilterType.LAST_MONTHS) {
      path = `${(SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_FREQUENCY_WEEKLY : API.MEMBERS_API.TRAINING_FREQUENCY_WEEKLY).replace('{memberId}', `${memberId}`)}?from=${periods[0].from}&to=${periods[periods.length - 1].to}`
    }

    return this.apiClient.get<TrainingFrequencyResponse>(path)
      .then((response: TrainingFrequencyResponse) => {
        let activities: any[] = []

        if (response.weekly_training_frequency) {
          // format daily values
          activities = Object.keys(response.weekly_training_frequency).map(day => {
            return [day, response.weekly_training_frequency ? response.weekly_training_frequency[day] : /* istanbul ignore next: only for the type check, the if above guarantees the value */ 0]
          })

        } else if (response.yearly_training_frequency) {
          // format monthly values
          activities = Object.keys(response.yearly_training_frequency).map(month => {
            return [month, response.yearly_training_frequency ? response.yearly_training_frequency[month] : /* istanbul ignore next: only for the type check, the if above guarantees the value */ 0]
          })
        }
        return Promise.resolve(activities)
      })
      .catch(error => {
        console.log('cannot retrieve training frequency', error)
        return Promise.reject(error)
      })
  }

  acceptTermsAndConditions (memberId, acceptance): Promise<void> {
    if (SessionManager.Instance.isManager()) {
      return this.updateMember(acceptance, memberId)
    } else {
      return this.updateProfile(acceptance)
    }
  }

  filterByAlerts = (member: Member, selectedFlags: Set<string>, showAll: boolean) => {
    if (selectedFlags.size === 0) {
      return showAll
    }
    let hasFlag: boolean = true
    selectedFlags.forEach(flag => {
      switch (flag) {
        case 'termsReacceptanceRequired':
          if (!member[flag]) {
            hasFlag = false
          }
          break
        case 'exercisesToChange':
          let changeUp = `${flag}Up`
          let changeDown = `${flag}Down`
          if (!(member.progression && ((member.progression[changeDown] > 0) || member.progression[changeUp] > 0))) {
            hasFlag = false
          }
          break
        case 'easyWorkoutAlert':
        case 'attendanceAlert14':
        case 'attendanceAlert30':
        case 'attendanceAlert60':
        case 'staleWorkoutAlert':
        case 'currentWorkoutAlert':
        case 'plannedCheckupAlert':
        case 'staleCheckupAlert':
          if (!(member.progression && member.progression[flag])) {
            hasFlag = false
          }
          break
        default:
        // do nothing
      }
    })
    return hasFlag
  }

  resetPassword (token: string, password: string): Promise<void> {
    let apiClient = new ApiClient(SessionManager.Instance.isManager() ? API.TRAINERS_API.RESET_PASSWORD : API.MEMBERS_API.RESET_PASSWORD, { skipAuth: true })
    return apiClient.post('', { reset_auth_token: token, password: password })
      .then(() => Promise.resolve())
      .catch(() => Promise.reject(null))
  }

  confirmEmailUpdate (token: string): Promise<void> {
    let apiClient = new ApiClient(SessionManager.Instance.isManager() ? API.TRAINERS_API.EMAIL : API.MEMBERS_API.EMAIL, { skipAuth: true })
    return apiClient.post('/confirm', { confirmation_token: token })
      .then(() => Promise.resolve())
      .catch(() => Promise.reject(null))
  }

  getMember (memberId) {
    return this.apiClient.get<MemberResponse>(API.TRAINERS_API.MEMBER_DETAILS.replace('{memberId}', memberId))
      .then((response: MemberResponse) => Promise.resolve(new Member(camelcaseKeys(response.member))))
      .catch(error => console.error('failed to retrieve member', error))
  }

  getRetentionFlagCounter = (flag, counters) => {
    if (!counters) return 0
    switch (flag) {
      case 'termsReacceptanceRequired':
        return counters.withTermsReacceptanceRequired
      case 'exercisesToChange':
        return counters.withExercisesToChangeDownAlert + counters.withExercisesToChangeUpAlert
      case 'easyWorkoutAlert':
        return counters.withEasyWorkoutAlert
      case 'attendanceAlert14':
        return counters.withAttendance14DaysAlert
      case 'attendanceAlert30':
        return counters.withAttendance30DaysAlert
      case 'attendanceAlert60':
        return counters.withAttendance60DaysAlert
      case 'staleWorkoutAlert':
        return counters.withStaleWorkoutAlert
      case 'currentWorkoutAlert':
        return counters.withCurrentWorkoutAlert
      case 'plannedCheckupAlert':
        return counters.withPlannedCheckupAlert
      case 'staleCheckupAlert':
        return counters.withStaleCheckupAlert
      default:
        return 0
    }
  }

  private updateMember (member, memberId?) {
    return this.apiClient.put(`${API.TRAINERS_API.MEMBERS}/${memberId}`, { member: snakecaseKeys(member) })
      .then(() => Promise.resolve())
      .catch(error => {
        console.log('error saving personal information updates', error)
        return Promise.reject(error)
      })
  }

  private updateProfile (member) {
    return this.apiClient.put(API.MEMBERS_API.PROFILE, { profile: snakecaseKeys(member) })
      .then(() => Promise.resolve())
      .catch(error => {
        console.log('error saving personal information updates', error)
        return Promise.reject(error)
      })
  }

}
